import * as moment from 'moment';
import * as _ from 'lodash';
import { DropdownButton, MenuItem, Nav } from "react-bootstrap";
import { ICenter } from '@nkr/models/index';
import { NavItemWrapper, NavWrapper } from './styles';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { OrganType } from '@nkr/enums/organ-type';
import { filter } from 'lodash';

interface IProps {
    tabKey: string,
    subTabKey: string,
    report: string,
    center: ICenter
}

const CenterReportsNav = (props: IProps) => {
    const { app: { site } } = useAppHookContext();

    if (props.center == null)
        return null;

    let referralYear = moment() >= moment('04/01/2023', 'MM/DD/YYYY') ? moment().year() : moment().year() - 1;

    let hrefPrefix = `/center/${props.center.id}/reports`;

    let isNkr = site.organType == OrganType.Kidney;

    let dashBoardReports = [
        { key: 'SUMMARY', title: 'Summary', href: hrefPrefix + '/dash-board/summary', show: true },
        { key: 'VOLUME', title: 'Volume', href: hrefPrefix + '/dash-board/volume', show: true },
        { key: 'CONTACTS', title: 'Contacts', href: hrefPrefix + '/dash-board/contacts', show: true },
        { key: 'PATIENT-DONORS', title: 'Registered Donors for Intended Recipients', href: hrefPrefix + '/dash-board/patient-donors?sd=&ed=&dt=0&st=2', show: isNkr },
        { key: 'COMPARISON', title: 'Comparison', href: hrefPrefix + '/dash-board/compare-conversion', show: true },
        { key: 'CENTER-FIVE-STAR-SURVEYS', title: 'Donor Satisfaction Survey Results (Your Center)', href: hrefPrefix + '/dash-board/center-five-star-surveys', show: isNkr },
        { key: 'NETWORK-FIVE-STAR-SURVEYS', title: 'Donor Satisfaction Survey Results (All Centers)', href: hrefPrefix + '/dash-board/network-five-star-surveys', show: isNkr },
        { key: 'PWL', title: 'PWL', href: hrefPrefix + '/dash-board/pwl-labbilling', show: true },
    ];

    let dashBoardReport = dashBoardReports.find(x => x.key == props.subTabKey);

    let allCenterReports = [
        { key: 'DIRECTORY', title: 'Directory', href: hrefPrefix + `/all-centers`, show: true },
        { key: 'COMPLIANCE', title: 'Compliance', href: hrefPrefix + '/all-centers/compliance', show: isNkr },
        { key: 'PRESELECT-HISTORY', title: 'Preselect History By Center', href: hrefPrefix + '/all-centers/preselect-history', show: isNkr },
        { key: 'CHAIN-END-DECLINE-RATIO', title: 'Waitlist Offer Decline Ratio (Last 6 Months)', href: hrefPrefix + '/all-centers/chain-end-decline-ratio', show: isNkr },
        { key: 'CHAIN-END-OFFERS', title: 'Chain End Offers (Last 12 Months)', href: hrefPrefix + '/all-centers/chain-end-offers', show: isNkr },
        { key: 'NKR-DONOR-REFERRAL-CONVERSION-RATE', title: 'NKR Donor Referral Conversion Rate (' + referralYear + ')', href: hrefPrefix + '/all-centers/nkr-donor-referral-conversion-rate', show: isNkr },
        { key: 'OVERALL-PERFORMANCE', title: 'Overall Performance - Rank in Network', href: hrefPrefix + '/all-centers/overall-performance', show: isNkr },
    ];

    let sortedAllCenterReports = _.sortBy(allCenterReports, x => x.title);
    let allCenterReport = null

    if (props.report == 'CENTER-SPECIFIC') {
        let index = sortedAllCenterReports.findIndex(x => x.key == 'DIRECTORY');
        let centerDetails = { key: 'CENTER-SPECIFIC', title: 'Center Details', href: '', show: true };

        sortedAllCenterReports = [...sortedAllCenterReports.slice(0, index + 1), centerDetails, ...sortedAllCenterReports.slice(index + 1)];
        allCenterReport = { ...centerDetails };
    } else {
        allCenterReport = sortedAllCenterReports.find(x => x.key == props.subTabKey);
    }

    let myCenterReports = [
        { key: 'LATE-SEROLOGIES', title: 'Serology Results Uploaded (< 3 Days Before Donor Surgery)', href: hrefPrefix + '/my-center/late-serologies', show: isNkr, parent: 'General Reports' },
        { key: 'SEROLOGY-FINAL-XM-BLOOD-DRAWS', title: 'Serology and Final XM Donor Blood Draws', href: hrefPrefix + '/my-center/serology-final-xm-blood-draws', show: isNkr, parent: 'General Reports' },
        { key: 'SEROLOGY-BLOOD-DRAWS', title: 'Serology Donor Blood Draws', href: hrefPrefix + '/my-center/serology-blood-draws', show: !isNkr, parent: 'General Reports' },
        { key: 'BLOOD-DRAWS-OUTSIDE-WINDOW', title: 'Serology Blood Draws Outside 5-7 Day Window', href: hrefPrefix + '/my-center/blood-draws-outside-window', show: isNkr, parent: 'General Reports' },
        { key: 'ON-TIME-KIDNEY-DETAIL', title: 'On Time Kidney Pickups (Last 3 Months)', href: hrefPrefix + '/my-center/on-time-kidney-pickup-detail', show: isNkr, parent: 'General Reports' },
        { key: 'COLD-ISCHEMIA-LAG-TIME-DETAIL', title: 'Cold Ischemia Lag Time for Moving Kidneys', href: hrefPrefix + '/my-center/cold-ischemia-lag-time-detail', show: isNkr, parent: 'General Reports' },
        { key: 'PRESELECT-REVERSAL-DETAIL', title: 'Preselect Reversals', href: hrefPrefix + '/my-center/preselect-reversal-detail', show: isNkr, parent: 'General Reports' },
        { key: 'SURGICAL-DATE-DECLINES', title: 'Surgical Date Declines', href: hrefPrefix + '/my-center/surgical-date-declines', show: isNkr, parent: 'General Reports' },
        { key: 'PRESELECT-DETAILS', title: '30 Day Preselect %', href: hrefPrefix + '/my-center/preselect-details', show: isNkr, parent: 'General Reports' },
        { key: 'CENTER-CHAIN-END-OFFERS', title: 'Chain End Offers (Last 12 Months)', href: hrefPrefix + '/my-center/chain-end-offers', show: isNkr, parent: 'General Reports' },
        { key: 'WAITLIST-AUDIT-REPORT', title: 'Waitlist Offer Decline Ratio (Last 6 Months)', href: hrefPrefix + '/my-center/waitlist-audit-report', show: isNkr, parent: 'General Reports' },
        { key: 'FCR-SCORE-AUDIT-REPORT', title: 'Center FCR Audit', href: hrefPrefix + '/my-center/fcr-score-audit-report', show: isNkr, parent: 'General Reports' },
        { key: 'CPR-SCORE-AUDIT-REPORT', title: 'Center CPR Audit', href: hrefPrefix + '/my-center/cpr-score-audit-report', show: isNkr, parent: 'General Reports' },
        { key: 'LATE-VXM', title: 'Donor Record Review & Virtual Crossmatch Results Recorded Late', href: hrefPrefix + '/my-center/late-vxm', show: isNkr, parent: 'General Reports' },
        { key: 'LATE-FINAL-XM', title: 'Final Crossmatch Results Uploaded < 3 Days Before Surgery', href: hrefPrefix + '/my-center/late-final-xm', show: isNkr, parent: 'General Reports' },
        { key: 'MISSED-MATCH-OFFERS', title: 'Missed Match Offer Deadline (Last 90 Days)', href: hrefPrefix + '/my-center/missed-match-offers', show: isNkr, parent: 'General Reports' },
        { key: 'NKR-AGREEMENTS', title: 'NKR Agreements', href: `/center/${props.center.id}/legal`, show: isNkr, parent: 'General Reports' },
        { key: 'NLR-AGREEMENTS', title: 'NLR Agreements', href: `/center/${props.center.id}/legal`, show: !isNkr, parent: 'General Reports' },
        { key: 'RCCAP', title: 'Swap Failure Rate Last 12 months', href: hrefPrefix + '/my-center/rccap', show: isNkr, parent: 'General Reports' },
        { key: 'TRANSPLANTED-PATIENTS', title: 'Transplanted Patients (Donors at your center)', href: hrefPrefix + '/my-center/transplanted-patients', show: isNkr, parent: 'General Reports' },
        { key: 'DATA-COMPLIANCE', title: 'Data Compliance Issues', href: hrefPrefix + '/my-center/data-compliance', show: isNkr, parent: 'General Reports' },
        { key: 'SWAP-QUALITY-ISSUES', title: 'Swap Quality Issues', href: hrefPrefix + '/my-center/swap-quality-issues', show: isNkr, parent: 'General Reports' },
        { key: 'NCS-SCORE', title: 'Net Chains Started (NCS) Score', href: hrefPrefix + '/my-center/ncs-score', show: isNkr, parent: 'Composite Score Reports' },
        { key: 'COMPOSITE-SCORE', title: 'Composite Score', href: hrefPrefix + '/my-center/composite-score', show: isNkr, parent: 'Composite Score Reports' },
        { key: 'TCE-FORMULA', title: 'Targeted Chain End (TCE) Formula', href: hrefPrefix + '/my-center/tce-formula', show: isNkr, parent: 'Composite Score Reports' },
    ];

    let filteredReports = myCenterReports.filter(x => x.show);

    let myCenterParents = [...new Set(myCenterReports.map(x => x.parent))].map(parent => {
        let groupReports = filteredReports.filter(x => x.parent == parent);

        return {
            parent,
            reports: parent == 'General Reports' ? _.sortBy(groupReports, x => x.title) : groupReports
        }
    }).filter(item => item.reports.length > 0);

    let myCenterReport = myCenterReports.find(x => x.key == props.subTabKey);

    return (
        <NavWrapper verticalAlign='center' minTabWidth='85' fontSize='12px' style={{ display: 'block' }}>
            <Nav bsStyle='tabs' className='center-tabs' activeKey={props.tabKey}>
                <NavItemWrapper active={props.tabKey == 'DASH-BOARD'} href={hrefPrefix + '/dash-board/summary'}>
                    <div>DASHBoard</div>
                </NavItemWrapper>

                <NavItemWrapper active={props.tabKey == 'ALL-CENTERS'} href={hrefPrefix + `/all-centers`}>
                    All Centers
                </NavItemWrapper>

                <NavItemWrapper active={props.tabKey == 'MY-CENTER'} href={isNkr ? `center/${props.center.id}/reports/my-center/transplanted-patients` : `/center/${props.center.id}/legal`}>
                    {isNkr ? 'My Center' : 'My Center (Legal)'}
                </NavItemWrapper>
            </Nav>

            {props.tabKey == 'DASH-BOARD' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 15px', backgroundColor: 'white' }}>
                    <div className='h3' style={{ margin: 0 }}>
                        {dashBoardReport?.title}
                    </div>

                    <div>
                        Report:&nbsp;
                        <DropdownButton pullRight title={dashBoardReport?.title} id='all-center-reports'>
                            {dashBoardReports.filter(x => x.show).map(item =>
                                <MenuItem active={item.key == props.subTabKey} href={item.href}>{item.title}</MenuItem>
                            )}
                        </DropdownButton>
                    </div>
                </div>
            )}

            {props.tabKey == 'ALL-CENTERS' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 15px', backgroundColor: 'white' }}>
                    <div className='h3' style={{ margin: 0 }}>{allCenterReport?.title}</div>

                    <div>
                        Report:&nbsp;
                        <DropdownButton pullRight title={allCenterReport?.title} id='all-center-reports'>
                            {sortedAllCenterReports.filter(x => x.show).map(item =>
                                <MenuItem active={item.key == allCenterReport.key} href={item.href}>{item.title}</MenuItem>
                            )}
                        </DropdownButton>
                    </div>
                </div>
            )}

            {props.tabKey == 'MY-CENTER' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 15px', backgroundColor: 'white' }}>
                    <div className='h3' style={{ margin: 0 }}>{myCenterReport?.title}</div>

                    <div>
                        Report:&nbsp;
                        <DropdownButton pullRight title={myCenterReport?.title} id='my-center-reports'>
                            {myCenterParents.map(item =>
                                <>
                                    {item.parent != '' && (
                                        <MenuItem header style={{ paddingLeft: 10 }}>{item.parent}</MenuItem>
                                    )}
                                    {item.reports.map(item =>
                                        <MenuItem active={item.key == props.subTabKey} href={item.href}>{item.title}</MenuItem>
                                    )}
                                </>
                            )}
                        </DropdownButton>
                    </div>
                </div>
            )}

        </NavWrapper>
    );
}

export default CenterReportsNav;